import * as React from 'react';

import classNames from 'classnames';

import type { CmlContent } from 'bundles/cml';
import ForumsCmlEditor from 'bundles/discussions/components/ForumsCmlEditor';
import DiscussionsCMLUtils from 'bundles/discussions/utils/DiscussionsCMLUtils';

import _t from 'i18n!nls/discussions';

import 'css!./__styles__/NewThreadForm';

type ForumObject = {
  id: string;
  title: string;
  parentForumId?: string;
};

type ChangeEvent = {
  value: string | CmlContent | ForumObject;
  isValid: boolean;
};

type ContentInputProps = {
  id?: string;
  courseId?: string;
  handleInputChange: (type: string, event: ChangeEvent) => void;
  cml: CmlContent;
  hasError: boolean;
  elemRefHook?: (elem: HTMLElement | null) => void;
};

class ContentInput extends React.Component<ContentInputProps> {
  handleInputChange = (cml: CmlContent) => {
    const { handleInputChange } = this.props;
    const event = {
      value: cml,
      isValid: DiscussionsCMLUtils.validateLength(cml),
    };
    handleInputChange('content', event);
  };

  getErrorMessage = (key?: string): string => {
    const translatedStrings: Record<string, string> = {
      TOO_SHORT: _t('Error: Please enter a longer thread description'),
      TOO_LONG: _t('Error: Please enter a shorter thread description'),
    };
    return key ? translatedStrings[key] : '';
  };

  renderError = (errorMessage?: string) => {
    const { cml, id } = this.props;
    return (
      <div className="c-form-error-message color-danger" id={`${id}-error-message`}>
        {errorMessage || this.getErrorMessage(DiscussionsCMLUtils.checkForErrors(cml))}
      </div>
    );
  };

  render() {
    const { id, cml, hasError, courseId } = this.props;
    const classes = classNames({
      'c-form': true,
      'c-form-textarea': true,
      'c-has-error': hasError,
    });
    const contentLengthWarning = DiscussionsCMLUtils.generateContentLengthWarning(cml);

    return (
      <div className={classes} aria-required={true} aria-invalid={hasError}>
        <ForumsCmlEditor
          ariaDescribedBy={`${id}-error-message`}
          ariaInvalid={hasError}
          ariaLabel={_t('Body')}
          ariaRequired="true"
          cml={cml}
          contentId={id}
          focusOnLoad={false} // since the forum title is the first input in the modal
          imageUploadOptions={DiscussionsCMLUtils.getImageUploadOptions(courseId)}
          placeholder={_t('Provide supporting details or context')}
          onChange={this.handleInputChange}
          renderMode={{
            typeName: 'resizable',
            minHeight: 140,
          }}
          borderColor={hasError ? 'var(--cds-color-red-800)' : undefined}
        />
        {hasError ? this.renderError() : contentLengthWarning}
      </div>
    );
  }
}

export default {
  ContentInput,
};

export { ContentInput };
